import React, { useState, useEffect } from 'react';
import { Radio, InputNumber, Space, Button } from 'antd';

interface RatioSelectorProps {
  defaultWidth?: number;
  defaultHeight?: number;
  defaultRatio?: string;
  selectedModel?: string; // 新增：模型类型
  onChange?: (width: number, height: number, ratio: string) => void;
  onConfirm?: (width: number, height: number, ratio: string) => void;
  onCancel?: () => void;
}

interface RatioOption {
  label: string;
  value: string;
  ratio: number; // 宽高比值
  defaultSize: { width: number; height: number };
}

const RatioSelector: React.FC<RatioSelectorProps> = ({
  defaultWidth = 1024,
  defaultHeight = 1024,
  defaultRatio = '1:1',
  selectedModel = 'gpt-image-1', // 新增：默认模型
  onChange,
  onConfirm,
  onCancel,
}) => {
  const [selectedRatio, setSelectedRatio] = useState<string>(defaultRatio);
  const [width, setWidth] = useState<number>(defaultWidth);
  const [height, setHeight] = useState<number>(defaultHeight);
  const [isCustom, setIsCustom] = useState<boolean>(false);

  // 预设比例选项
  const ratioOptions: RatioOption[] = [
    {
      label: '16:9',
      value: '16:9',
      ratio: 16 / 9,
      defaultSize: { width: 1820, height: 1024 },
    },
    {
      label: '3:2',
      value: '3:2',
      ratio: 3 / 2,
      defaultSize: { width: 1536, height: 1024 },
    },
    {
      label: '4:3',
      value: '4:3',
      ratio: 4 / 3,
      defaultSize: { width: 1365, height: 1024 },
    },
    {
      label: '1:1',
      value: '1:1',
      ratio: 1,
      defaultSize: { width: 1024, height: 1024 },
    },
    {
      label: '3:4',
      value: '3:4',
      ratio: 3 / 4,
      defaultSize: { width: 1024, height: 1356 },
    },
    {
      label: '2:3',
      value: '2:3',
      ratio: 2 / 3,
      defaultSize: { width: 1024, height: 1536 },
    },
    {
      label: '9:16',
      value: '9:16',
      ratio: 9 / 16,
      defaultSize: { width: 1024, height: 1820 },
    },
  ];

  // 根据模型过滤可用的比例选项
  const getAvailableOptions = () => {
    if (selectedModel === 'gpt-image-1') {
      // gpt-image-1 只支持 1:1, 2:3, 3:2
      return ratioOptions.filter((option) =>
        ['1:1', '2:3', '3:2'].includes(option.value),
      );
    }
    if (selectedModel === 'nano-banana') {
      return ratioOptions.filter((option) =>
        ['1:1', '3:4', '4:3', '9:16', '16:9'].includes(option.value),
      );
    }
    // 其他模型支持所有比例
    return ratioOptions;
  };

  const availableOptions = getAvailableOptions();

  // 检查当前选中的比例是否在可用选项中
  const isCurrentRatioAvailable = availableOptions.some(
    (option) => option.value === selectedRatio,
  );

  // 如果当前比例不可用，切换到第一个可用选项
  useEffect(() => {
    if (!isCurrentRatioAvailable && availableOptions.length > 0) {
      const firstAvailable = availableOptions[0];
      setSelectedRatio(firstAvailable.value);
      setWidth(firstAvailable.defaultSize.width);
      setHeight(firstAvailable.defaultSize.height);
      setIsCustom(false);

      if (onChange) {
        onChange(
          firstAvailable.defaultSize.width,
          firstAvailable.defaultSize.height,
          firstAvailable.value,
        );
      }
    }
  }, [selectedModel, availableOptions, isCurrentRatioAvailable]);

  // 处理比例选择
  const handleRatioChange = (value: string) => {
    // 检查该比例是否可用
    const isAvailable = availableOptions.some(
      (option) => option.value === value,
    );
    if (!isAvailable) return;

    setSelectedRatio(value);
    setIsCustom(false);

    const option = availableOptions.find((opt) => opt.value === value);
    if (option) {
      setWidth(option.defaultSize.width);
      setHeight(option.defaultSize.height);

      // 触发回调
      if (onChange) {
        onChange(option.defaultSize.width, option.defaultSize.height, value);
      }
    }
  };

  // 处理宽度变化
  const handleWidthChange = (newWidth: number | null) => {
    if (newWidth === null) return;

    setWidth(newWidth);
    setIsCustom(true);

    // 如果有选中的比例，按比例调整高度
    if (selectedRatio !== 'custom') {
      const option = availableOptions.find(
        (opt) => opt.value === selectedRatio,
      );
      if (option) {
        const newHeight = Math.round(newWidth / option.ratio);
        setHeight(newHeight);

        if (onChange) {
          onChange(newWidth, newHeight, selectedRatio);
        }
      }
    } else {
      if (onChange) {
        onChange(newWidth, height, 'custom');
      }
    }
  };

  // 处理高度变化
  const handleHeightChange = (newHeight: number | null) => {
    if (newHeight === null) return;

    setHeight(newHeight);
    setIsCustom(true);

    // 如果有选中的比例，按比例调整宽度
    if (selectedRatio !== 'custom') {
      const option = availableOptions.find(
        (opt) => opt.value === selectedRatio,
      );
      if (option) {
        const newWidth = Math.round(newHeight * option.ratio);
        setWidth(newWidth);

        if (onChange) {
          onChange(newWidth, newHeight, selectedRatio);
        }
      }
    } else {
      if (onChange) {
        onChange(width, newHeight, 'custom');
      }
    }
  };

  // 初始化时触发回调
  useEffect(() => {
    if (onChange) {
      onChange(width, height, selectedRatio);
    }
  }, []); // 只在组件挂载时执行一次

  return (
    <div
      style={{
        background: '#2c2c2c',
        padding: '16px',
        borderRadius: '8px',
        color: 'white',
      }}
    >
      {/* 标题 */}
      <div
        style={{
          marginBottom: '12px',
          fontSize: '14px',
          fontWeight: '500',
        }}
      >
        选择比例
        {selectedModel === 'gpt-image-1' && (
          <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
            (gpt-image-1 支持的比例)
          </span>
        )}
      </div>

      {/* 比例选择器 */}
      <div style={{ marginBottom: '16px' }}>
        <div
          style={{
            display: 'flex',
            gap: '8px',
            overflowX: 'auto',
            paddingBottom: '4px',
          }}
        >
          {ratioOptions.map((option) => {
            // 检查该比例是否可用
            const isAvailable = availableOptions.some(
              (opt) => opt.value === option.value,
            );

            // 计算在固定容器中的矩形尺寸
            const containerSize = 32;
            let rectWidth, rectHeight;

            if (option.ratio >= 1) {
              // 横向矩形：宽度固定，高度按比例缩放
              rectWidth = containerSize;
              rectHeight = containerSize / option.ratio;
            } else {
              // 纵向矩形：高度固定，宽度按比例缩放
              rectHeight = containerSize;
              rectWidth = containerSize * option.ratio;
            }

            return (
              <div
                key={option.value}
                onClick={() => isAvailable && handleRatioChange(option.value)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: isAvailable ? 'pointer' : 'not-allowed',
                  padding: '8px',
                  borderRadius: '6px',
                  backgroundColor:
                    selectedRatio === option.value && isAvailable
                      ? '#1890ff'
                      : 'transparent',
                  transition: 'all 0.2s ease',
                  minWidth: '50px',
                  flexShrink: 0,
                  opacity: isAvailable ? 1 : 0.3, // 不可用时降低透明度
                }}
              >
                {/* 固定尺寸的容器 */}
                <div
                  style={{
                    width: `${containerSize}px`,
                    height: `${containerSize}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {/* 按比例显示的圆角矩形 */}
                  <div
                    style={{
                      width: `${rectWidth}px`,
                      height: `${rectHeight}px`,
                      backgroundColor:
                        selectedRatio === option.value && isAvailable
                          ? 'white'
                          : isAvailable
                          ? '#666'
                          : '#444', // 不可用时使用更深的颜色
                      borderRadius: '3px',
                      transition: 'all 0.2s ease',
                    }}
                  />
                </div>

                {/* 比例数字 */}
                <span
                  style={{
                    fontSize: '11px',
                    color:
                      selectedRatio === option.value && isAvailable
                        ? 'white'
                        : isAvailable
                        ? '#999'
                        : '#555', // 不可用时使用更深的颜色
                    fontWeight:
                      selectedRatio === option.value && isAvailable
                        ? '500'
                        : '400',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {option.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* 尺寸输入 */}
      <div style={{ marginBottom: '16px' }}>
        <div
          style={{
            fontSize: '12px',
            color: '#999',
            marginBottom: '8px',
          }}
        >
          尺寸
        </div>
        <Space align="center">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: '#999' }}>W</span>
            <InputNumber
              value={width}
              onChange={handleWidthChange}
              min={1}
              max={10000}
              step={1}
              style={{
                width: '80px',
                background: '#404040',
                borderColor: '#555',
                color: 'white',
              }}
              controls={false}
            />
          </div>

          <div
            style={{
              width: '20px',
              height: '1px',
              background: '#666',
              margin: '0 4px',
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: '#999' }}>H</span>
            <InputNumber
              value={height}
              onChange={handleHeightChange}
              min={1}
              max={10000}
              step={1}
              style={{
                width: '80px',
                background: '#404040',
                borderColor: '#555',
                color: 'white',
              }}
              controls={false}
            />
          </div>

          <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
            PX
          </span>
        </Space>
      </div>

      {/* 当前比例显示 */}
      <div
        style={{
          fontSize: '12px',
          color: '#999',
          textAlign: 'right',
          marginBottom: '16px',
        }}
      >
        {isCustom ? '自定义比例' : `生成比例 ${selectedRatio}`}
      </div>

      {/* 底部操作按钮 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          paddingTop: '16px',
          borderTop: '1px solid #404040',
        }}
      >
        <Button
          size="small"
          onClick={onCancel}
          style={{
            backgroundColor: '#404040',
            borderColor: '#555',
            color: 'white',
          }}
        >
          取消
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={() => {
            if (onConfirm) {
              onConfirm(width, height, selectedRatio);
            }
          }}
        >
          确定
        </Button>
      </div>
    </div>
  );
};

export default RatioSelector;
